import React, { useEffect } from 'react';
import styles from './ConfirmationDialog.module.css';

export interface ConfirmationDialogProps {
  id: string;
  title: string;
  message: string;
  confirmText?: string;
  secondaryConfirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onSecondaryConfirm?: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  id,
  title,
  message,
  confirmText = 'אישור',
  secondaryConfirmText,
  cancelText = 'ביטול',
  onConfirm,
  onSecondaryConfirm,
  onCancel,
  type = 'danger'
}) => {
  const hasSecondary = Boolean(secondaryConfirmText && onSecondaryConfirm);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
        return;
      }
      if (event.key === 'Enter') {
        onConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onConfirm, onCancel]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return '⚠️';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '⚠️';
    }
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog} role="dialog" aria-labelledby={`dialog-title-${id}`} aria-describedby={`dialog-message-${id}`}>
        <div className={styles.header}>
          <div className={styles.icon}>{getIcon()}</div>
          <h3 id={`dialog-title-${id}`} className={styles.title}>{title}</h3>
        </div>
        
        <div id={`dialog-message-${id}`} className={styles.message}>
          {message}
        </div>
        
        <div className={`${styles.actions} ${hasSecondary ? styles.actionsStacked : ''}`}>
          <button
            type="button"
            className={`${styles.button} ${styles.cancelButton}`}
            onClick={onCancel}
            autoFocus
          >
            {cancelText}
          </button>
          {hasSecondary && (
            <button
              type="button"
              className={`${styles.button} ${styles.confirmButton} ${styles[type]} ${styles.secondaryConfirmButton}`}
              onClick={onSecondaryConfirm}
            >
              {secondaryConfirmText}
            </button>
          )}
          <button
            type="button"
            className={`${styles.button} ${styles.confirmButton} ${styles[type]}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
